import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useApiErrorHandler from "../useApiErrorHandler";
import useLoading from "../useLoading";
import useAlerts from "../useAlerts";
import EnabledDocumentTypesService from "../../services/dte/enabledDocumentTypesService";

import {
  setEnabledDocuments,
  addEnabledDocument,
  toggleDocumentState,
  updateFormField,
  setFormErrors,
  clearFormErrors,
  resetForm,
  setSubmit,
  openCreateModal,
  closeCreateModal,
} from "../../store/slices/dte/enabledDocumentTypesSlice";

export const useEnabledDocumentTypes = () => {
  const dispatch: AppDispatch = useDispatch();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();

  // ==============================
  // STATE
  // ==============================
  const {
    enabledDocuments,
    form,
    formErrors,
    isSubmit,
    isCreateModalOpen,
  } = useSelector((state: RootState) => state.enabledDocumentTypes);

  // ==============================
  // FETCH LIST
  // ==============================
  const fetchEnabledDocuments = async () => {
    try {
      showLoadingIndicator();
      const response = await EnabledDocumentTypesService.getAll();
      console.log(response);
      const { statusCode, data } = response;
      if (statusCode === 200) dispatch(setEnabledDocuments(data));
    } catch (err) {
      handle(err);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ==============================
  // VALIDATION
  // ==============================
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.documentTypeId || form.documentTypeId === 0) {
      errors.documentTypeId = "Debe seleccionar un tipo de documento.";
    }

    dispatch(setFormErrors(errors));
    return Object.keys(errors).length === 0;
  };

  // ==============================
  // CREATE
  // ==============================
  const saveDocument = async () => {
    try {
      dispatch(clearFormErrors());
      if (!validate()) return;

      dispatch(setSubmit(true));

      const response = await EnabledDocumentTypesService.save(form.documentTypeId);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(addEnabledDocument(data));
        dispatch(resetForm());
        dispatch(closeCreateModal());
        showAlert(type, title, message);
      }
    } catch (err) {
      handle(err);
    } finally {
      dispatch(setSubmit(false));
    }
  };

  // ==============================
  // TOGGLE STATUS
  // ==============================
  const toggleDocument = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await EnabledDocumentTypesService.toggleStatus(id);
      console.log(response);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(toggleDocumentState(data));
        showAlert(type, title, message);
      }
    } catch (err) {
      handle(err);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ==============================
  // MODAL CONTROL
  // ==============================
  const openModalCreate = () => {
    dispatch(resetForm());
    dispatch(clearFormErrors());
    dispatch(openCreateModal());
  };

  const closeModalCreate = () => {
    dispatch(closeCreateModal());
    dispatch(resetForm());
    dispatch(clearFormErrors());
  };


  const cleanFormErrors = () => {
    dispatch(clearFormErrors());
  }

  // ==============================
  // FORM UPDATE
  // ==============================
  const updateField = (field: string, value: any) =>
    dispatch(updateFormField({ field: field as any, value }));

  return {
    // state
    enabledDocuments,
    form,
    formErrors,
    isSubmit,
    isCreateModalOpen,

    // crud
    fetchEnabledDocuments,
    saveDocument,
    toggleDocument,

    // form
    updateField,

    cleanFormErrors,

    // modal
    openModalCreate,
    closeModalCreate,
  };
};
